import { useEffect } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { ExternalLink, Pencil, UserMinus, UserPlus, X } from 'lucide-react';
import { Link } from 'react-router-dom';

import { CAPABILITY_OPTIONS } from '@/api/capabilities.api';
import { listMetricEntries, listMetrics, type Metric, type MetricEntry } from '@/api/scorecards.api';
import type { SeatView } from '@/api/seats.api';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { departmentDotColor, roleLabel, type SeatGwc } from '@/lib/accountability/seatMeta';
import { metricStatus, type MetricStatus } from '@/lib/scorecards/status';

/**
 * Quick-peek drawer for a seat: purpose, responsibilities, role +
 * capability chips, measurables count, and the seat controls (assign /
 * unassign, edit, mark-hiring). Read for everyone; the mutating controls
 * render only for admins.
 */

const CAP_LABEL = new Map(CAPABILITY_OPTIONS.map((c) => [c.value as string, c.label]));

export interface SeatDrawerProps {
  seat: SeatView | null;
  departmentName?: string | null;
  gwc?: SeatGwc | null;
  canManage: boolean;
  isMutating?: boolean;
  onClose: () => void;
  onEdit: (seat: SeatView) => void;
  onAssign: (seat: SeatView) => void;
  onUnassign: (seat: SeatView) => void;
  onToggleHiring: (seat: SeatView) => void;
}

export function SeatDrawer({
  seat,
  departmentName,
  gwc,
  canManage,
  isMutating = false,
  onClose,
  onEdit,
  onAssign,
  onUnassign,
  onToggleHiring,
}: SeatDrawerProps) {
  useEffect(() => {
    if (!seat) return undefined;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [seat, onClose]);

  if (!seat) return null;

  const holder = seat.holderEmployee;

  return (
    <div className="fixed inset-0 z-40 flex print:hidden">
      <button
        type="button"
        aria-label="Close drawer"
        onClick={onClose}
        className="flex-1 animate-fade-in bg-black/30"
      />
      <aside
        role="dialog"
        aria-label={`${seat.name} seat`}
        className="ml-auto flex h-full w-full max-w-md animate-slide-up flex-col overflow-y-auto bg-white shadow-kw-lg sm:animate-fade-in"
      >
        {/* Header */}
        <header className="flex items-start gap-3 border-b border-kw-border p-5">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="typo-h3">{seat.name}</h2>
              {seat.holderEmployeeId ? null : seat.hiring ? (
                <Badge variant="warning">Hiring</Badge>
              ) : (
                <Badge variant="neutral">Vacant</Badge>
              )}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-kw-muted">
              <span className="inline-flex items-center rounded-kw-full bg-kw-bg px-2 py-0.5 font-medium text-kw-shark">
                {roleLabel(seat.systemRole)}
              </span>
              <span className="inline-flex items-center gap-1">
                <span
                  className="size-2 rounded-full"
                  style={{ backgroundColor: departmentDotColor(seat.departmentId) }}
                  aria-hidden
                />
                {departmentName ?? 'No department'}
              </span>
              {seat.scenario === 'future' ? <Badge variant="info">Future</Badge> : null}
            </div>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="rounded p-1 text-kw-muted hover:bg-kw-bg hover:text-kw-shark"
          >
            <X className="size-5" />
          </button>
        </header>

        <div className="flex-1 space-y-5 p-5">
          {/* Holder */}
          <section>
            <h3 className="text-[12px] font-medium uppercase tracking-wide text-kw-muted">Seated</h3>
            {holder ? (
              <div className="mt-1 space-y-0.5">
                <p className="text-[14px] text-kw-shark">
                  {holder.firstName} {holder.lastName}
                </p>
                <a
                  href={`mailto:${holder.email}`}
                  className="text-[12px] text-kw-muted hover:text-kw-shark hover:underline"
                >
                  {holder.email}
                </a>
                {holder.membershipId === null ? (
                  <p className="text-[12px] italic text-kw-warning-text">
                    Directory only — access applies when they accept a portal invite.
                  </p>
                ) : null}
                {gwc ? (
                  <p className="pt-1 text-[12px] text-kw-muted">
                    GWC: <GwcText label="Gets it" v={gwc.getsIt} /> ·{' '}
                    <GwcText label="Wants it" v={gwc.wantsIt} /> ·{' '}
                    <GwcText label="Capacity" v={gwc.capacity} />
                  </p>
                ) : null}
              </div>
            ) : (
              <p className="mt-1 text-[13px] text-kw-ghost">This seat is vacant.</p>
            )}
          </section>

          {/* Purpose */}
          <section>
            <h3 className="text-[12px] font-medium uppercase tracking-wide text-kw-muted">Purpose</h3>
            <p className="mt-1 text-[13px] text-kw-shark">
              {seat.purpose?.trim() ? seat.purpose : <span className="italic text-kw-ghost">Not set.</span>}
            </p>
          </section>

          {/* Responsibilities */}
          <section>
            <h3 className="text-[12px] font-medium uppercase tracking-wide text-kw-muted">
              Roles &amp; responsibilities
            </h3>
            {seat.responsibilities.length > 0 ? (
              <ul className="mt-1 list-disc space-y-0.5 pl-4 text-[13px] text-kw-shark">
                {seat.responsibilities.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            ) : (
              <p className="mt-1 text-[13px] italic text-kw-ghost">None listed.</p>
            )}
          </section>

          {/* Access */}
          <section>
            <h3 className="text-[12px] font-medium uppercase tracking-wide text-kw-muted">
              Access this seat grants
            </h3>
            {seat.capabilities.length > 0 ? (
              <div className="mt-1 flex flex-wrap gap-1.5">
                {seat.capabilities.map((c) => (
                  <span
                    key={c}
                    className="inline-flex items-center rounded-kw-full bg-kw-cerulean-light px-2 py-0.5 text-[11px] font-medium text-kw-cerulean-dark"
                  >
                    {CAP_LABEL.get(c) ?? c}
                  </span>
                ))}
              </div>
            ) : (
              <p className="mt-1 text-[13px] italic text-kw-ghost">
                Role access only — no extra capability bundle.
              </p>
            )}
          </section>

          {/* Scorecard (measurables) */}
          <section>
            <h3 className="text-[12px] font-medium uppercase tracking-wide text-kw-muted">Scorecard</h3>
            <SeatScorecard seatId={seat.id} />
          </section>

          {/* Controls */}
          {canManage ? (
            <section className="flex flex-wrap gap-2 border-t border-kw-border pt-4">
              {seat.holderEmployeeId ? (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={isMutating}
                  onClick={() => {
                    onUnassign(seat);
                  }}
                >
                  <UserMinus className="size-4" />
                  Unassign
                </Button>
              ) : (
                <Button
                  size="sm"
                  disabled={isMutating}
                  onClick={() => {
                    onAssign(seat);
                  }}
                >
                  <UserPlus className="size-4" />
                  Assign someone
                </Button>
              )}
              {!seat.holderEmployeeId ? (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={isMutating}
                  onClick={() => {
                    onToggleHiring(seat);
                  }}
                >
                  {seat.hiring ? 'Stop hiring' : 'Mark as hiring'}
                </Button>
              ) : null}
              <Button
                size="sm"
                variant="secondary"
                disabled={isMutating}
                onClick={() => {
                  onEdit(seat);
                }}
              >
                <Pencil className="size-4" />
                Edit seat
              </Button>
              {holder ? (
                <Link
                  to={`/employees/${holder.id}`}
                  className="inline-flex h-9 items-center gap-1.5 rounded-kw-md border border-kw-border bg-white px-3 text-[13px] font-medium text-kw-shark hover:border-kw-cerulean/40 hover:text-kw-cerulean"
                >
                  <ExternalLink className="size-4" />
                  Holder profile
                </Link>
              ) : null}
            </section>
          ) : holder ? (
            <section className="border-t border-kw-border pt-4">
              <Link
                to={`/employees/${holder.id}`}
                className="inline-flex items-center gap-1.5 text-[13px] font-medium text-kw-cerulean hover:underline"
              >
                <ExternalLink className="size-4" />
                Open holder profile
              </Link>
            </section>
          ) : null}
        </div>
      </aside>
    </div>
  );
}

/**
 * The seat's measurables, pulled live from the scorecard (ADR-028). Lists
 * each metric with its goal and the latest recorded value coloured by
 * status. Fans out one entries query per metric — a seat carries only a
 * handful, so the cost is trivial and TanStack Query dedupes with the
 * main Scorecards grid.
 */
function SeatScorecard({ seatId }: { seatId: string }) {
  const metricsQuery = useQuery({
    queryKey: ['scorecards', 'metrics', 'seat', seatId],
    queryFn: () => listMetrics({ seatId }),
  });
  const metrics = metricsQuery.data ?? [];

  const entriesQueries = useQueries({
    queries: metrics.map((m) => ({
      queryKey: ['scorecards', 'entries', m.id],
      queryFn: () => listMetricEntries(m.id),
    })),
  });

  if (metricsQuery.isLoading) {
    return <p className="mt-1 text-[13px] text-kw-muted">Loading measurables…</p>;
  }
  if (metricsQuery.error) {
    return (
      <p className="mt-1 text-[13px] text-kw-danger-text">Couldn&apos;t load this seat&apos;s scorecard.</p>
    );
  }
  if (metrics.length === 0) {
    return <p className="mt-1 text-[13px] italic text-kw-ghost">No measurables on this seat yet.</p>;
  }

  return (
    <ul className="mt-2 space-y-2">
      {metrics.map((m, i) => {
        const latest = latestEntry(entriesQueries[i]?.data ?? []);
        const status = latest ? metricStatus(latest.value, m.goal, m.direction) : null;
        return (
          <li key={m.id} className="rounded-kw-md border border-kw-border bg-white p-2.5">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate text-[13px] font-medium text-kw-shark">{m.title}</p>
                <p className="text-[11px] text-kw-muted">
                  Goal {formatMetricValue(m.goal, m)}{' '}
                  <span className="text-kw-ghost">({m.direction === 'higher_is_better' ? '↑' : '↓'})</span>
                </p>
              </div>
              {latest ? (
                <span
                  className={`shrink-0 rounded-kw-full px-2 py-0.5 text-[11px] font-medium ${statusTone(status)}`}
                >
                  {formatMetricValue(latest.value, m)}
                </span>
              ) : (
                <span className="shrink-0 text-[11px] text-kw-ghost">No data</span>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/** The most recently recorded entry (max `weekOf`), or null if none. */
function latestEntry(entries: MetricEntry[]): MetricEntry | null {
  let best: MetricEntry | null = null;
  for (const e of entries) {
    if (!best || e.weekOf > best.weekOf) best = e;
  }
  return best;
}

function formatMetricValue(value: number, metric: Pick<Metric, 'unit' | 'unitLabel'>): string {
  switch (metric.unit) {
    case 'dollars':
      return value.toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      });
    case 'percent':
      return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
    case 'ratio':
      return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
    case 'custom':
      return `${value.toLocaleString()} ${metric.unitLabel ?? ''}`.trim();
    case 'count':
    default:
      return value.toLocaleString();
  }
}

function statusTone(status: MetricStatus | null): string {
  switch (status) {
    case 'green':
      return 'bg-kw-emerald-light text-kw-tag-text';
    case 'yellow':
      return 'bg-kw-warning-bg text-kw-warning-text';
    case 'red':
      return 'bg-kw-danger-bg text-kw-danger-text';
    default:
      return 'text-kw-ghost';
  }
}

function GwcText({ label, v }: { label: string; v: boolean | null }) {
  const cls = v === true ? 'text-kw-tag-text' : v === false ? 'text-kw-danger-text' : 'text-kw-ghost';
  return (
    <span className={cls} title={label}>
      {label.charAt(0)}
      {v === null ? '?' : v ? '✓' : '✗'}
    </span>
  );
}
